import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import "dayjs/locale/sv";
import _ from "lodash";
import { observer } from "mobx-react";
import React, { useMemo } from "react";
import { StyleSheet } from "react-native";
import { Icon } from "~/components/Icon";
import { Spacer } from "~/components/Spacer";
import { Pressable } from "~/components/V2/Pressable";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useResponsive } from "~/hooks/useResponsive";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";

const S = StyleSheet.create({
  weekContainer: {
    borderBottomWidth: 2,
    borderColor: "#e6e6e6d9",
    paddingBottom: 16,
  },
  dayConatiner: {
    borderWidth: 2,
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  dayText: {
    lineHeight: 0,
  },
  spinnerContainer: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
});

export const Calendar = observer(function Calendar({
  startDay,
  onPressNextWeek,
  onPressPreviousWeek,
  selectedDay,
  onPressSelectDay,
  setStartDay,
  clinicTimezone,
}: {
  startDay: dayjs.Dayjs;
  onPressNextWeek: () => void;
  onPressPreviousWeek: () => void;
  selectedDay: dayjs.Dayjs | null;
  onPressSelectDay: (day: dayjs.Dayjs) => void;
  setStartDay: React.Dispatch<React.SetStateAction<dayjs.Dayjs>>;
  clinicTimezone: string;
}) {
  const store = useStore();
  const r = useResponsive();
  const language = intl.locale;

  const clinicId = store.bookingStore.clinicId;
  const reasonId = store.bookingStore.reasonId;

  const practitionerId = store.bookingStore.practitionerId;

  const query = useQuery({
    queryKey: ["availableDays", { clinicId, reasonId, practitionerId }],
    queryFn: async () => {
      const { firstAvailableDay, availableDays } =
        await store.timeslotsStore.getAvailability(practitionerId);

      if (!firstAvailableDay) {
        return null;
      }

      const firstAvailableDayInClinicTimezone = dayjs
        .utc(firstAvailableDay, "YYYY-MM-DDTHH:mm:ss[Z]")
        .tz(clinicTimezone)
        .startOf("day");

      // Set the state to the first available day
      setStartDay(firstAvailableDayInClinicTimezone);
      onPressSelectDay(firstAvailableDayInClinicTimezone);

      return { firstAvailableDay, availableDays };
    },
  });

  const startOfWeek = startDay.startOf("week").add(1, "day");
  const currentMonth = _.capitalize(startDay.locale(language).format("MMMM"));
  const monthAtTheEndOfWeek = _.capitalize(
    startDay.add(6, "day").locale(language).format("MMMM"),
  );
  const currentYear = startDay.locale(language).format("YYYY");
  const yearAtTheEndOfWeek = startDay
    .add(6, "day")
    .locale(language)
    .format("YYYY");

  const weekDays = Array.from({ length: 7 }).map((_, index) => {
    const day = startOfWeek.add(index, "day");
    return day;
  });

  const availableDays = query.data?.availableDays ?? new Set();

  return (
    <View style={S.weekContainer}>
      {query.isLoading && (
        <View centerContent style={S.spinnerContainer}>
          <SpinnerV2 />
        </View>
      )}

      {/* Display month and year */}
      <CalendarHeader
        currentMonth={currentMonth}
        monthAtTheEndOfWeek={monthAtTheEndOfWeek}
        currentYear={currentYear}
        yearAtTheEndOfWeek={yearAtTheEndOfWeek}
      />
      <Spacer large />
      {/* Date picker */}
      <View flexDirectionRow>
        <Pressable
          centerContent
          style={{ width: r(40, undefined, undefined) }}
          onPress={onPressPreviousWeek}
        >
          <Icon
            name="chevron-left"
            color={styleConstantsV2.colorPrimary}
            size={24}
          />
        </Pressable>
        <View flex flexDirectionRow>
          {/* Dayjs */}
          {weekDays.map((day) => {
            const dateStr = day.format("YYYY-MM-DD");
            const isPast = day.isBefore(dayjs(), "day");
            const isToday = day.isSame(dayjs(), "day");
            const isSelected =
              selectedDay?.get("D") === day.get("D") &&
              selectedDay?.get("M") === day.get("M") &&
              selectedDay?.get("year") === day.get("year");
            const isAvailableDay = availableDays.has(dateStr);
            const isOpaqueText = !isAvailableDay || isPast;

            return (
              <Pressable
                disabled={isPast}
                flex
                key={day.toISOString()}
                centerContent
                onPress={() => {
                  onPressSelectDay(day);
                }}
              >
                <BodyText colorBlack sizeSmall weightBold colorGrey>
                  {day.locale(language).format("ddd").toUpperCase()}
                </BodyText>
                <Spacer large />
                <View
                  centerContent
                  style={[
                    {
                      borderColor: isToday
                        ? styleConstantsV2.colorPrimary
                        : "transparent",

                      backgroundColor: isSelected
                        ? styleConstantsV2.colorPrimary
                        : "transparent",
                    },
                    S.dayConatiner,
                  ]}
                >
                  <BodyText
                    sizeMedium
                    colorBlack={isSelected === false}
                    colorWhite={isSelected === true}
                    weightBold
                    style={{
                      color: isOpaqueText ? "#e6e6e6d9" : undefined,
                      ...S.dayText,
                    }}
                  >
                    {day.format("D")}
                  </BodyText>
                </View>
              </Pressable>
            );
          })}
        </View>

        <Pressable
          centerContent
          style={{ width: r(40, undefined, undefined) }}
          onPress={onPressNextWeek}
        >
          <Icon
            name="chevron-right"
            color={styleConstantsV2.colorPrimary}
            size={24}
          />
        </Pressable>
      </View>
    </View>
  );
});

interface CalendarHeaderProps {
  currentMonth: string;
  monthAtTheEndOfWeek: string;
  currentYear: string;
  yearAtTheEndOfWeek: string;
}

const getCalendarHeaderText = ({
  currentMonth,
  currentYear,
  monthAtTheEndOfWeek,
  yearAtTheEndOfWeek,
}: {
  currentMonth: string;
  currentYear: string;
  monthAtTheEndOfWeek: string;
  yearAtTheEndOfWeek: string;
}) => {
  if (currentYear !== yearAtTheEndOfWeek) {
    return `${currentMonth} ${currentYear}/${monthAtTheEndOfWeek} ${yearAtTheEndOfWeek}`;
  } else if (currentMonth !== monthAtTheEndOfWeek) {
    return `${currentMonth}/${monthAtTheEndOfWeek} ${currentYear}`;
  }
  return `${currentMonth} ${currentYear}`;
};

const CalendarHeader = observer(function CalendarHeader({
  currentMonth,
  monthAtTheEndOfWeek,
  currentYear,
  yearAtTheEndOfWeek,
}: CalendarHeaderProps) {
  const headerText = useMemo(() => {
    return getCalendarHeaderText({
      currentMonth,
      currentYear,
      monthAtTheEndOfWeek,
      yearAtTheEndOfWeek,
    });
  }, [currentMonth, currentYear, monthAtTheEndOfWeek, yearAtTheEndOfWeek]);

  return (
    <View colorGrey10 paddingVertical16 centerContent>
      <BrandText sizeH3 colorBlack weightBold>
        {headerText}
      </BrandText>
    </View>
  );
});
